"use client";

import { useEffect, useId, useRef, useState } from "react";
import { usePortalTranslations } from "../i18n/client";
import styles from "./PortalShareButton.module.css";

type ShareChannel = "facebook" | "x" | "whatsapp" | "linkedin" | "email";
type CopyState = "idle" | "copying" | "copied" | "manual";

function ShareIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4" /></svg>;
}

function ChannelIcon({ channel }: { channel: ShareChannel }) {
  if (channel === "email") {
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 7 8 6 8-6" /></svg>;
  }
  if (channel === "facebook") {
    return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h8v-7H9v-3h3V8.5C12 5.6 13.7 4 16.2 4c1.2 0 2.3.1 2.6.1v3h-1.7c-1.4 0-1.6.7-1.6 1.6V11h3.2l-.4 3h-2.8v7H20a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1Z" /></svg>;
  }
  if (channel === "x") {
    return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 3H22l-6.8 7.8L23 21h-6.3l-4.9-6.4L6.2 21H3l7.3-8.4L2.8 3h6.5l4.4 5.8L18.9 3Zm-1.1 16.2h1.7L8.4 4.7H6.6l11.2 14.5Z" /></svg>;
  }
  if (channel === "linkedin") {
    return <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1ZM8.4 18H5.6V9.5h2.8V18ZM7 8.3a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2ZM18.4 18h-2.8v-4.2c0-1 0-2.2-1.4-2.2s-1.6 1-1.6 2.1V18H9.8V9.5h2.7v1.2h.1c.4-.7 1.3-1.4 2.5-1.4 2.7 0 3.3 1.8 3.3 4.1V18Z" /></svg>;
  }
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.5 11.7a8.5 8.5 0 0 1-12.7 7.4L3 20.5l1.4-4.7a8.5 8.5 0 1 1 16.1-4.1Z" /><path d="m8 7.7 1.4-.2 1.1 2.6-1 .9a8 8 0 0 0 3.5 3.4l.9-1 2.6 1.2-.2 1.4c-.1.9-1 1.5-1.9 1.3-4.3-.8-7.3-3.8-8.1-8.1-.2-.9.5-1.7 1.7-1.5Z" /></svg>;
}

export function PortalShareButton({ portalName, portalSlug }: { portalName: string; portalSlug: string }) {
  const t = usePortalTranslations();
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const copyRequestRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copyState, setCopyState] = useState<CopyState>("idle");

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const previouslyFocused = document.activeElement;
    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    closeButtonRef.current?.focus({ preventScroll: true });

    return () => {
      copyRequestRef.current += 1;
      dialog.close();
      document.body.style.overflow = previousOverflow;
      const returnTarget = trigger ?? previouslyFocused;
      if (returnTarget instanceof HTMLElement && returnTarget.isConnected) returnTarget.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (copyState !== "copied") return;
    const timeout = window.setTimeout(() => setCopyState("idle"), 4000);
    return () => window.clearTimeout(timeout);
  }, [copyState]);

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedName = encodeURIComponent(portalName);
  const channels: { channel: ShareChannel; label: string; href: string }[] = [
    { channel: "facebook", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
    { channel: "x", label: "X", href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedName}` },
    { channel: "whatsapp", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(`${portalName}\n${shareUrl}`)}` },
    { channel: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
    { channel: "email", label: t("portal.share.email"), href: `mailto:?subject=${encodedName}&body=${encodeURIComponent(`${portalName}\n${shareUrl}`)}` },
  ];

  async function copyLink() {
    const request = ++copyRequestRef.current;
    setCopyState("copying");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(shareUrl);
      if (request === copyRequestRef.current && dialogRef.current?.open) setCopyState("copied");
    } catch {
      if (request !== copyRequestRef.current || !dialogRef.current?.open) return;
      inputRef.current?.focus();
      inputRef.current?.select();
      setCopyState("manual");
    }
  }

  return <>
    <button
      ref={triggerRef}
      type="button"
      className={styles.trigger}
      aria-haspopup="dialog"
      onClick={() => {
        setShareUrl(`${window.location.origin}/${encodeURIComponent(portalSlug)}`);
        setCopyState("idle");
        setOpen(true);
      }}
    >
      <span className={styles.triggerIcon}><ShareIcon /></span>
      <span className={styles.triggerLabel}>{t("portal.share.label")}</span>
    </button>
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      onCancel={(event) => { event.preventDefault(); setOpen(false); }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const elements = [...event.currentTarget.querySelectorAll<HTMLElement>("a[href], button:not(:disabled), input:not(:disabled)")];
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) setOpen(false);
      }}
    >
      <div className={styles.header}>
        <div>
          <h2 id={`${id}-title`}>{t("portal.share.title")}</h2>
          <p id={`${id}-description`}>{portalName}</p>
        </div>
        <button ref={closeButtonRef} type="button" className={styles.close} onClick={() => setOpen(false)} aria-label={t("portal.share.close")}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </div>
      <div className={styles.content}>
        <ul className={styles.channels} aria-label={t("portal.share.options")}>
          {channels.map(({ channel, label, href }) => <li key={channel}>
            <a href={href} target={channel === "email" ? undefined : "_blank"} rel={channel === "email" ? undefined : "noopener noreferrer"} aria-label={t("portal.share.channel", {channel: label})}>
              <span className={`${styles.channelIcon} ${styles[channel]}`}><ChannelIcon channel={channel} /></span>
              <span>{label}</span>
            </a>
          </li>)}
        </ul>
        <div className={styles.copySection}>
          <label htmlFor={`${id}-link`}>{t("portal.share.copyAlternative")}</label>
          <div className={styles.copyField}>
            <input ref={inputRef} id={`${id}-link`} value={shareUrl} readOnly onFocus={(event) => event.currentTarget.select()} aria-describedby={`${id}-feedback`} />
            <button type="button" onClick={() => void copyLink()} disabled={copyState === "copying"} aria-label={copyState === "copied" ? t("portal.share.copiedLabel") : t("portal.share.copy")}>
              {copyState === "copied"
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="7" width="12" height="14" rx="2" /><path d="M15 7V5a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h2" /></svg>}
            </button>
          </div>
          <p id={`${id}-feedback`} role="status" className={styles.feedback}>
            {copyState === "copied" ? t("portal.share.copied") : copyState === "manual" ? t("portal.share.manualCopy") : copyState === "copying" ? t("portal.share.copying") : ""}
          </p>
        </div>
      </div>
    </dialog>
  </>;
}
